import { useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

/**
 * NeuralBrain – an abstract, premium "brain" formed from displaced particles
 * with two electric color cores (blue + violet) and orbiting synapse arcs.
 * Built imperatively to keep babel-injected JSX metadata off R3F primitives.
 */
function buildBrainGroup() {
  const root = new THREE.Group();
  const inner = new THREE.Group();
  root.add(inner);

  // === Particle brain ===
  const COUNT = 1300;
  const positions = new Float32Array(COUNT * 3);
  const colors = new Float32Array(COUNT * 3);
  const c1 = new THREE.Color("#4690D9");
  const c2 = new THREE.Color("#9CC6F0");
  const c3 = new THREE.Color("#CFE3FB");

  for (let i = 0; i < COUNT; i++) {
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);

    let r = 1.55 + (Math.random() - 0.5) * 0.18;
    const pinch = 1 - Math.pow(Math.sin(theta * 0.5), 2) * 0.18;
    r *= pinch;

    let x = r * Math.sin(phi) * Math.cos(theta);
    let y = r * Math.cos(phi) * 0.78;
    let z = r * Math.sin(phi) * Math.sin(theta);

    const n =
      Math.sin(x * 3.0) * 0.06 +
      Math.cos(y * 3.4) * 0.06 +
      Math.sin(z * 2.7) * 0.06;
    const dir = new THREE.Vector3(x, y, z).normalize();
    x += dir.x * n;
    y += dir.y * n;
    z += dir.z * n;

    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;

    const t = (x + 1.6) / 3.2;
    const col = c1.clone().lerp(c2, t);
    if (Math.random() < 0.08) col.copy(c3);
    colors[i * 3] = col.r;
    colors[i * 3 + 1] = col.g;
    colors[i * 3 + 2] = col.b;
  }

  const pGeom = new THREE.BufferGeometry();
  pGeom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  pGeom.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const pMat = new THREE.PointsMaterial({
    vertexColors: true,
    size: 0.034,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const particles = new THREE.Points(pGeom, pMat);
  inner.add(particles);

  // === Core glow spheres ===
  const makeGlow = (color, pos, opacity) => {
    const g = new THREE.SphereGeometry(0.32, 32, 32);
    const m = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const s = new THREE.Mesh(g, m);
    s.position.set(...pos);
    return s;
  };
  inner.add(makeGlow("#4690D9", [-0.4, 0, 0], 0.12));
  inner.add(makeGlow("#9CC6F0", [0.4, 0, 0], 0.1));

  // (outer halo sphere removed — it was clipping at the canvas edges and
  // creating a visible rectangular boundary when the brain canvas shrank
  // on later slides.)

  // === Synapse arcs ===
  const arcsGroup = new THREE.Group();
  inner.add(arcsGroup);

  const arcs = [];
  for (let i = 0; i < 6; i++) {
    const a = Math.random() * Math.PI * 2;
    const b = a + Math.PI * (0.4 + Math.random() * 0.6);
    const r = 1.62 + Math.random() * 0.06;
    const tiltX = (Math.random() - 0.5) * Math.PI * 0.5;
    const tiltY = (Math.random() - 0.5) * Math.PI * 0.5;
    const color = Math.random() > 0.5 ? "#4690D9" : "#9CC6F0";
    const speed = 0.4 + Math.random() * 0.6;

    const grp = new THREE.Group();
    grp.rotation.set(tiltX, tiltY, 0);

    // line
    const segs = 48;
    const arcPos = new Float32Array(segs * 3);
    for (let k = 0; k < segs; k++) {
      const t = k / (segs - 1);
      const ang = a + (b - a) * t;
      arcPos[k * 3] = Math.cos(ang) * r;
      arcPos[k * 3 + 1] = Math.sin(ang * 1.3) * 0.18;
      arcPos[k * 3 + 2] = Math.sin(ang) * r;
    }
    const lGeom = new THREE.BufferGeometry();
    lGeom.setAttribute("position", new THREE.BufferAttribute(arcPos, 3));
    const lMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
    });
    const line = new THREE.Line(lGeom, lMat);
    grp.add(line);

    // dot
    const dGeom = new THREE.SphereGeometry(0.025, 12, 12);
    const dMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
    });
    const dot = new THREE.Mesh(dGeom, dMat);
    grp.add(dot);

    arcsGroup.add(grp);
    arcs.push({ a, b, r, speed, dot });
  }

  return { root, inner, particles, arcsGroup, arcs };
}

function BrainSceneObjects() {
  const { scene } = useThree();
  const builtRef = useRef(null);

  useEffect(() => {
    const built = buildBrainGroup();
    scene.add(built.root);
    builtRef.current = built;
    return () => {
      scene.remove(built.root);
      built.root.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose?.());
          else obj.material.dispose?.();
        }
      });
      builtRef.current = null;
    };
  }, [scene]);

  useFrame((state, delta) => {
    const built = builtRef.current;
    if (!built) return;
    built.inner.rotation.y += delta * 0.08;
    built.inner.rotation.x = Math.sin(state.clock.elapsedTime * 0.25) * 0.08;
    built.arcsGroup.rotation.y = state.clock.elapsedTime * 0.04;
    if (built.particles?.material) {
      built.particles.material.opacity =
        0.78 + Math.sin(state.clock.elapsedTime * 1.8) * 0.08;
    }
    // animate dots along arcs
    for (const arc of built.arcs) {
      const tNorm = ((state.clock.elapsedTime * arc.speed) % 2) / 2;
      const ang = arc.a + (arc.b - arc.a) * tNorm;
      arc.dot.position.set(
        Math.cos(ang) * arc.r,
        Math.sin(ang * 1.3) * 0.18,
        Math.sin(ang) * arc.r,
      );
    }
  });

  // add ambient and accent lights via scene.add once
  useEffect(() => {
    const amb = new THREE.AmbientLight(0xffffff, 0.4);
    const p1 = new THREE.PointLight(0x4690d9, 1.2);
    p1.position.set(3, 2, 2);
    const p2 = new THREE.PointLight(0x9cc6f0, 1.1);
    p2.position.set(-3, -2, 2);
    scene.add(amb, p1, p2);
    return () => {
      scene.remove(amb, p1, p2);
    };
  }, [scene]);

  return null;
}

export default function Brain3D({ paused = false }) {
  return (
    <Canvas
      camera={{ position: [0, 0, 6.4], fov: 38 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      style={{ width: "100%", height: "100%" }}
      frameloop={paused ? "demand" : "always"}
      data-testid="brain-3d-canvas"
    >
      <BrainSceneObjects />
    </Canvas>
  );
}
